/**
 * Central app state with a tiny pub/sub.
 *
 * Stages: 'folder' | 'extract' | 'review' | 'card' | 'export'
 *
 * File record shape:
 *   {
 *     id, name, size, handle, file,
 *     status:    'pending' | 'working' | 'done' | 'failed',
 *     thumbDataUrl, pageCount,
 *     extracted: { vendor_name, invoice_number, ... } | null,
 *     coding:    { glCode, propertyCode, source: 'rule'|'manual'|null, ruleId? } | null,
 *     error:     string | null,
 *     ms:        number | null,
 *     edits:     { vendor_name?, invoice_number?, ... }   // user overrides
 *   }
 */

import { getSessionApiKey, isEncryptedKeyStored } from './lib/crypto.js';

const listeners = new Set();

const state = {
  stage: 'folder',
  appConfigHandle: null,
  workHandle: null,
  workName: null,
  files: [],
  // API key state — derived from crypto module, never persisted as plaintext
  hasKey: () => !!getSessionApiKey(),
  keyEverSetUp: () => isEncryptedKeyStored(),
  cardIndex: -1,
  isExtracting: false,
  // Settings (mirrored to IndexedDB by settings.js; defaults here)
  settings: {
    matchStrategy:   'normalized',  // 'exact' | 'normalized' | 'fuzzy'
    matchThreshold:  0.85,
    applyMode:       'auto',        // 'auto' | 'suggest'
    dateFormat:      'YYYY-MM-DD',
    csvBom:          false,
    model:           'claude-sonnet-4-6',
    csvFilenamePrefix: 'invoices',
    /** Rotating rules JSON files under app-config/backups/ (1–20). */
    backupRuleGenerations: 5,
    /** 'light' | 'dark' | 'system' */
    themePreference: 'system'
  },
  // Modal stack (simple LIFO)
  modal: null
};

export function getState() { return state; }

export function setState(patch) {
  Object.assign(state, patch);
  emit();
}

export function updateFile(id, patch) {
  const f = state.files.find(x => x.id === id);
  if (f) Object.assign(f, patch);
  emit();
}

export function setSettings(patch) {
  state.settings = { ...state.settings, ...patch };
  emit();
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit() {
  for (const fn of listeners) {
    try { fn(state); } catch (err) { console.error('state listener threw', err); }
  }
}
